import {
  useGetLocationListNameIdQuery,
  useGetPostListNameIdQuery,
  useGetProfileListNameIdQuery,
  type ActionPayload,
  type ActionPayloadLocationProfileChanged,
  type ActionPayloadPostEnabled,
} from "@package/ui-gql/index-data-access";
import { useMemo } from "react";

function isPostEnabledAction(
  action: ActionPayload | null | undefined,
): action is ActionPayloadPostEnabled {
  return action?.__typename === "ActionPayloadPostEnabled";
}

function isLocationProfileChangedAction(
  action: ActionPayload | null | undefined,
): action is ActionPayloadLocationProfileChanged {
  return action?.__typename === "ActionPayloadLocationProfileChanged";
}

export type SchedulerEntityNames = {
  posts: Map<string, string>;
  locations: Map<string, string>;
  profiles: Map<string, string>;
  loading: boolean;
};

function toIdLabelMap<T extends { id?: string | null }>(
  items: T[] | undefined,
  getLabel: (item: T) => string | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();

  for (const item of items ?? []) {
    if (item.id) {
      map.set(item.id, getLabel(item) ?? item.id);
    }
  }

  return map;
}

function uniqueIds(ids: (string | null | undefined)[]): string[] {
  return Array.from(new Set(ids.filter((id): id is string => !!id)));
}

/**
 * ActionPayloads only carry ids -- resolving them to human-readable labels
 * for the preview card is the panel's job, same as every grid column that
 * joins a related list client-side (see harbor-cms-ui).
 *
 * Each list is filtered to the ids this event's actions actually reference
 * (the same `filter: { ids }` pattern EventsGrid/LocationAnalyticsDrilldown
 * use), not an unfiltered `perPage: 1000` sweep of the whole org -- besides
 * the wasted payload, an unfiltered sweep silently truncates in an org with
 * more than 1000 posts/locations/profiles, which would render raw ids in
 * the "what this changes" sentence this hook exists to avoid.
 */
export function useSchedulerEntityNames(
  actions: (ActionPayload | null | undefined)[],
): SchedulerEntityNames {
  const postIds = useMemo(
    () => uniqueIds(actions.filter(isPostEnabledAction).map((a) => a.post)),
    [actions],
  );
  const locationIds = useMemo(
    () =>
      uniqueIds(
        actions.filter(isLocationProfileChangedAction).map((a) => a.location),
      ),
    [actions],
  );
  const profileIds = useMemo(
    () =>
      uniqueIds(
        actions.filter(isLocationProfileChangedAction).map((a) => a.profile),
      ),
    [actions],
  );

  const { data: postData, loading: loadingPosts } = useGetPostListNameIdQuery({
    variables: { filter: { ids: postIds }, page: 0, perPage: 1000 },
    skip: !postIds.length,
  });
  const { data: locationData, loading: loadingLocations } =
    useGetLocationListNameIdQuery({
      variables: { filter: { ids: locationIds }, page: 0, perPage: 1000 },
      skip: !locationIds.length,
    });
  const { data: profileData, loading: loadingProfiles } =
    useGetProfileListNameIdQuery({
      variables: { filter: { ids: profileIds }, page: 0, perPage: 1000 },
      skip: !profileIds.length,
    });

  const posts = useMemo(
    () => toIdLabelMap(postData?.items, (post) => post.title),
    [postData?.items],
  );
  const locations = useMemo(
    () => toIdLabelMap(locationData?.items, (location) => location.name),
    [locationData?.items],
  );
  const profiles = useMemo(
    () => toIdLabelMap(profileData?.items, (profile) => profile.name),
    [profileData?.items],
  );

  return {
    posts,
    locations,
    profiles,
    loading: loadingPosts || loadingLocations || loadingProfiles,
  };
}
